import { Cookies } from 'react-cookie';
import {
  generateUUID,
  IAuthRelativePath,
  AppConstants,
  I18nUtils,
} from '@amzn/work-spaces-web-client-core-lib';
import { CoreFactory } from '@bridge/factory/CoreFactory';
import { BLOCK_URL_BASE_LIST } from '@/presession/authentication/types';

const cookie = new Cookies();
const logger = CoreFactory.getLogger();

export class WarpDriveExternalNativeAuthUrlBuilder {
  private readonly base: string;
  private redirectUrl: URL;
  private readonly isBaseUrlClientBlockerPage: boolean;

  constructor(base: string) {
    this.base = base;
    this.isBaseUrlClientBlockerPage =
      WarpDriveExternalNativeAuthUrlBuilder.isBaseUrlSoftBlockUrl(this.base);
    this.redirectUrl = this.extractRedirectUrl();
  }

  reset() {
    this.redirectUrl = new URL(this.base);
  }

  // not using this for unified signin
  setRedirectUri(strategy: IAuthRelativePath) {
    // if its a hardblock page then do not set the redirect uri
    if (this.isBaseUrlClientBlockerPage && !this.hasRedirectUrl()) {
      return;
    }
    const port = window.location.port ? `:${window.location.port}` : '';

    this.redirectUrl.searchParams.set(
      'redirect_uri',
      `https://${window.location.hostname}${port}/auth/${strategy}`
    );
  }

  setRelayStateUrl(relayStateName: string, relayStateUrl: string) {
    this.redirectUrl.searchParams.set(relayStateName, relayStateUrl);
  }

  setLocale() {
    const language = cookie.get('language');
    // Use the AWSCShortbread locale mapping, which defaults to 'en_US' if language is undefined
    const locale = I18nUtils.mapToAWSCShortbreadLocale(language);
    this.redirectUrl.searchParams.set('locale', locale);
  }

  setCsrfToken(csrfToken: string | null = null, addToUrl: boolean = true) {
    // If no token is provided, generate one
    const token = csrfToken ?? generateUUID();

    // Always set the cookie
    cookie.set(AppConstants.COOKIE_CSRF_TOKEN, token, { path: '/' });

    // Only add to URL if requested (for WD_LOGIN flow)
    if (addToUrl) {
      this.redirectUrl.searchParams.set('state', token);
    }

    return token;
  }

  getUrl() {
    if (this.isBaseUrlClientBlockerPage) {
      const baseUrl = new URL(this.base);
      // Only set the redirectUrl if it is present in the base url
      if (this.hasRedirectUrl()) {
        // This is the softblock scenario where the broker sends the redirectUrl for the URL with Unsupported_continue.html
        logger.info(`This is for Softblock page.`);
        const actualAuthRedirectionUrlString = this.redirectUrl.toString();
        baseUrl.searchParams.set('redirectUrl', actualAuthRedirectionUrlString);
        logger.info(
          `Generated auth redirection url for client block ${baseUrl}`
        );
      } else {
        // This is the hardblock scenario where the broker doesn't send any redirectUrl for the URL with Unsupported.html
        logger.info(
          `This is for Hardblock page: No redirectUrl found, returning original base URL: ${baseUrl}`
        );
      }
      return baseUrl;
    } else {
      logger.info(`Returning the URL for the normal workflow`);
      return this.redirectUrl;
    }
  }

  hasRedirectUrl(): boolean {
    const baseUrl = new URL(this.base);
    return baseUrl.searchParams.has('redirectUrl');
  }

  private extractRedirectUrl() {
    if (this.isBaseUrlClientBlockerPage) {
      const baseUrl = new URL(this.base);
      const redirectionUrlString = baseUrl.searchParams.get(
        'redirectUrl'
      ) as string;

      // If the redirectUrl is present in the baseUrl
      if (redirectionUrlString) {
        const decodedUrlString = decodeURIComponent(redirectionUrlString);
        return new URL(decodedUrlString);
      }

      // If there is no redirectUrl in the baseUrl, simply return the base url
      return baseUrl;
    } else {
      return new URL(this.base);
    }
  }

  private static isBaseUrlSoftBlockUrl(baseUrl: string): boolean {
    let isConnectivityWebSite = false;
    BLOCK_URL_BASE_LIST.forEach((connectivityUrl) => {
      if (baseUrl?.toLowerCase().includes(connectivityUrl)) {
        logger.info(
          `Identified client blocker scenario for externalAuthRedirectionUrl ${baseUrl}`
        );
        isConnectivityWebSite = true;
      }
    });
    return isConnectivityWebSite;
  }
}
